import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AvatarBadge } from "@/components/Avatars";
import { X, Send, Users, Loader2 } from "lucide-react";

export default function AssignModal({ lesson, onClose, onDone }) {
  const [students, setStudents] = useState(null);
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!lesson) return;
    setPicked([]); setNote(""); setQ("");
    api.listStudents().then((s) => setStudents(s.filter((x) => x.role === "student" || x.username))).catch(() => setStudents([]));
  }, [lesson]);

  if (!lesson) return null;
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const shown = (students || []).filter((s) => {
    const t = q.trim().toLowerCase();
    return !t || (s.name || "").toLowerCase().includes(t) || (s.username || "").toLowerCase().includes(t);
  });

  const assign = async () => {
    if (picked.length === 0) { toast.error("Pick at least one student."); return; }
    setBusy(true);
    try {
      const res = await api.assignLesson({ lesson_id: lesson.id, student_ids: picked, note });
      toast.success(`Assigned "${lesson.title}" to ${res.assigned_to} student${res.assigned_to > 1 ? "s" : ""}.`);
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1F2937]/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <motion.div initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} className="w-full max-w-md rounded-[28px] bg-white p-6 shadow-2xl border border-[#E5DECF]" data-testid="assign-modal">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-xl font-extrabold text-[#1F2937]">Assign lesson</h3>
            <button onClick={onClose} data-testid="assign-close" className="rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={20} /></button>
          </div>
          <p className="mt-1 font-body text-sm text-[#6B7280]">“{lesson.title}” will appear on the chosen student's home screen.</p>

          {students && students.length > 6 && (
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="assign-search" placeholder="Search students…" className="mt-3 w-full rounded-xl border-2 border-[#E5DECF] px-3 py-2 font-body text-sm outline-none focus:border-[#0D9488]" />
          )}

          <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1" data-testid="assign-student-list">
            {students === null && <div className="py-6 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>}
            {students && students.length === 0 && (
              <div className="flex flex-col items-center rounded-2xl border border-dashed border-[#E5DECF] p-6 text-center">
                <Users size={28} className="text-[#94A3B8]" />
                <p className="mt-2 font-body text-sm text-[#94A3B8]">No students yet. Add students first.</p>
              </div>
            )}
            {students && students.length > 0 && shown.length === 0 && <p className="py-4 text-center text-sm text-[#94A3B8]">No students match “{q}”.</p>}
            {shown.map((s) => {
              const on = picked.includes(s.id);
              return (
                <button key={s.id} onClick={() => toggle(s.id)} data-testid={`assign-pick-${s.username || s.id}`}
                  className={`flex w-full items-center gap-3 rounded-2xl border-2 px-3 py-2.5 text-left transition-colors ${on ? "border-[#0D9488] bg-[#0D9488]/5" : "border-[#E5DECF] hover:bg-[#F2ECE1]"}`}>
                  <AvatarBadge avatarKey={s.avatar} size={34} />
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-sm font-bold text-[#1F2937]">{s.name}</div>
                    <div className="text-xs text-[#6B7280]">@{s.username}{s.grade ? ` · ${s.grade}` : ""}</div>
                  </div>
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${on ? "border-[#0D9488] bg-[#0D9488] text-white" : "border-[#CBD5E1]"}`}>{on && "✓"}</span>
                </button>
              );
            })}
          </div>

          <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="assign-note" placeholder="Optional note for the student…" className="mt-3 w-full rounded-xl border-2 border-[#E5DECF] px-3 py-2 font-body text-sm outline-none focus:border-[#0D9488]" />

          <button onClick={assign} disabled={busy || (students && students.length === 0)} data-testid="assign-submit" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-3 font-heading font-bold text-white hover:bg-[#0B7F74] disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Assign to {picked.length || ""} student{picked.length === 1 ? "" : "s"}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
